import React from "react"
import {
  Box,
  InfoIcon,
  Link,
  ListItemText,
  Stack,
  Typography,
  UnorderedList,
  UnorderedListItem,
  useTheme
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useFlags } from "launchdarkly-react-client-sdk"

const WHEEL_RECORDS_REQUEST_FORM_URL =
  "https://wheelvirtualcareservices.zendesk.com/hc/en-us/article_attachments/36884848141332"
const WHEEL_SUPPORT_EMAIL = "support@wheel.com"
const BANNER_TITLE_ID = "wheel-transition-info-banner-title"

// Custom rather than Carotene's Alert: design borrows the info Alert's colors and icon but not its layout or text color.
// This page is retired on 12/1 (MEM-1065), so this banner goes with it.
export function WheelTransitionInfoBanner(): JSX.Element | null {
  const intl = useIntl()
  const theme = useTheme()
  const { wheelSunset } = useFlags()

  if (!wheelSunset) {
    return null
  }

  return (
    <Box
      component="section"
      aria-labelledby={BANNER_TITLE_ID}
      sx={{
        display: "flex",
        gap: theme.spacing(theme.tokens.spacing.md),
        padding: theme.spacing(theme.tokens.spacing.lg),
        backgroundColor: theme.palette.info.light,
        borderRadius: theme.tokens.borderRadius.md
      }}
    >
      <InfoIcon sx={{ color: theme.palette.info.main }} />
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Stack spacing={theme.spacing(theme.tokens.spacing.xs)}>
          <Typography id={BANNER_TITLE_ID} variant="h5" component="h2" color="text.primary">
            <FormattedMessage defaultMessage="Virtual menopause care is moving on December 1" />
          </Typography>
          <Typography>
            <FormattedMessage defaultMessage="Starting Tuesday, December 1, 2026, you’ll book virtual appointments with Carrot clinicians who specialize in menopause, right here in the Carrot app. You can keep booking via the links below for dates through November 30." />
          </Typography>
        </Stack>
        <Stack spacing={theme.spacing(theme.tokens.spacing.xs)}>
          <Typography variant="h6" component="h3" color="text.primary">
            <FormattedMessage defaultMessage="If you’ve had visits here before:" />
          </Typography>
          <UnorderedList sx={{ paddingBlock: 0, "li::marker": { color: theme.palette.info.main } }}>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage({
                  defaultMessage:
                    "Your first appointment after December 1 will be an initial visit with a new clinician."
                })}
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage({
                  defaultMessage:
                    "If you’ll need a refill, make sure you have enough medication to last until that visit."
                })}
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage(
                  {
                    defaultMessage:
                      "To get a copy of your records, email a completed <formLink>request form</formLink> to <emailLink>{wheelSupportEmail}</emailLink>, our current telemedicine provider."
                  },
                  {
                    wheelSupportEmail: WHEEL_SUPPORT_EMAIL,
                    formLink: (linkContent) => (
                      <Link href={WHEEL_RECORDS_REQUEST_FORM_URL} target="_blank" rel="noopener noreferrer">
                        {linkContent}
                      </Link>
                    ),
                    emailLink: (linkContent) => <Link href={`mailto:${WHEEL_SUPPORT_EMAIL}`}>{linkContent}</Link>
                  }
                )}
              />
            </UnorderedListItem>
          </UnorderedList>
        </Stack>
      </Stack>
    </Box>
  )
}
